import { apiClient } from "@/api/client";
import useGlobalStore from "@/store/globalStore";
import { AxiosError } from "axios";
import { create } from "zustand";

export interface Team {
  id: string;
  name: string;
  team_type: string;
  media_agency_id: string;
  media_agency_name?: string;
  media_agency?: MediaAgency;
}

export interface MediaAgency {
  id: string;
  name: string;
  logo_url?: string;
}

interface TeamsState {
  teams: Array<Team> | null;
  sortField: string;
  setTeams: (teams: Array<Team>) => void;
  getTeams: (sort?: string) => Promise<void>;
  createTeam: (name: string, team_type: string) => Promise<void>;
  updateTeam: (team: Team) => Promise<void>;
  deleteTeam: (id: string) => Promise<void>;
  /**
   * Move a user from `from_team_id` to `to_team_id` within the active agency.
   * Backend (TeamTransferService) verifies same-agency + source-membership and
   * runs the move atomically. Throws on 422 / 403.
   */
  transferMember: (
    userId: string,
    fromTeamId: string,
    toTeamId: string
  ) => Promise<TransferMembershipResponse>;
}

export interface TransferMembershipResponse {
  message: string;
  membership: {
    id: string;
    team_id: string;
    user_id: string;
    role_id: string;
    active: boolean;
  };
}

export interface TeamsRespose {
  teams: Array<Team>;
}

// store/teamsStore.ts
const useTeamStore = create<TeamsState>((set, get) => ({
  teams: [],
  sortField: '',
  setTeams: (teams) => {
    set({ teams });
  },

  getTeams: async (sort?: string) => {
    try {
      const params = sort ? { sort } : {};
      const response = await apiClient.get<TeamsRespose>("/teams", { params });
      set({ teams: response.data.teams, sortField: sort || '' });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.error || "Something went wrong"
      );
    }
  },

  createTeam: async (name: string, team_type: string) => {
    try {
      const response = await apiClient.post("/teams", { name, team_type });
      const newTeam = response.data.team;

      set((state) => ({
        teams: [...(state.teams ?? []), newTeam],
      }));
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Something went wrong"
      );
    }
  },

  updateTeam: async (team: Team) => {
    try {
      const response = await apiClient.put(`/teams/${team.id}`, team);
      const updatedTeam = response.data.team;

      set((state) => ({
        teams: (state.teams ?? []).map((t) =>
          t.id === team.id ? updatedTeam : t
        ),
      }));
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Something went wrong"
      );
    }
  },

  deleteTeam: async (id: string) => {
    try {
      await apiClient.delete(`/teams/${id}`);
      set((state) => ({
        teams: (state.teams ?? []).filter((team) => team.id !== id),
      }));
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Something went wrong"
      );
    }
  },

  transferMember: async (
    userId: string,
    fromTeamId: string,
    toTeamId: string
  ) => {
    try {
      const response = await apiClient.post<TransferMembershipResponse>(
        "/teams/transfer",
        {
          user_id: userId,
          from_team_id: fromTeamId,
          to_team_id: toTeamId,
        }
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.errors?.team_id?.[0] ||
        axiosError.response?.data?.errors?.user_id?.[0] ||
        "Failed to transfer team member.";
      throw new Error(errorMessage);
    }
  },
}));

export default useTeamStore;
